import _ from 'lodash';
import axios from 'axios';
import sha256 from 'crypto-js/sha256';
import Raven from 'raven-js';
import moment from 'moment';

import store from './../store/configureStore';
import ActionTypes from './../actions/types';
import { getUserInfoURL } from '../utils/restUrls';
import LoadingMessageImage from '../../html/assets/images/msr-loading@2x.gif';
import { DEFAULT_STATE_INIT_INTERVAL, DEFAULT_USER_INIT_INTERVAL, LOGO_URLS, UIDCLUB,  } from '../constants';
import { addToToaster } from './../actions/toasters';
const Image = LOGO_URLS.Image.startsWith('http') ? LOGO_URLS.Image : require(`../../html/assets/images/${LOGO_URLS.Image}`);
const Image2 = LOGO_URLS.Image2.startsWith('http') ? LOGO_URLS.Image2 : require(`../../html/assets/images/${LOGO_URLS.Image2}`);

export const UUID = () => {};
UUID._count = 0;
UUID.get = (prefix='uuid') => `${prefix}id__${++UUID._count}`;

export const convertSecTimeStampToMiliSec = (timestamp) => timestamp * 1000;

export const checkUserLoggedIn = (user) => !!user.profileId;

export const isInvalidDate = (dt) => isNaN(dt.getTime());

export const formatDate = (dt) => moment(dt).format('MMM Do, h:mma');

export const fromNow = (dt, suffix=false) => moment(dt).fromNow(suffix);

export const removeLastSlashFromUrl = (url) => url.replace(/(\/?\s*)$/, '');

export const checkIsBookingOpen = (dtmOpen, dtmClose) => {
  let isBookingOpen = false;
  let eventType = '';
  const currentTime = new Date();
  const openTime = new Date(Number(dtmOpen));
  const closeTime = new Date(Number(dtmClose));

  if (dtmOpen && dtmClose && !isInvalidDate(openTime) && !isInvalidDate(closeTime)) {
    const isOpenTimeValid = (currentTime >= openTime);
    const isCloseTimeValid = (currentTime <= closeTime);
    isBookingOpen = (isOpenTimeValid && isCloseTimeValid);
    if(!isOpenTimeValid) {
      eventType = 'parking_will_open';
    }else if(!isCloseTimeValid) {
      eventType = 'parking_closed';
    }else if(isBookingOpen) {
      eventType = 'parking_open';
    }
  }
  return {
    isBookingOpen,
    eventType,
    openTime,
    closeTime,
  };
};

export const logException = (ex, context) => {
  Raven.captureException(ex, {
    extra: context
  });
  /*eslint no-console:0*/
  window.console && console.error && console.error(ex);
};

export const logFailure = (message, context) => {
  Raven.captureMessage(message, {
    extra: context
  });
  /*eslint no-console:0*/
  window.console && console.log && console.log(context);
};



export const spotStateChecksum = (uidEvent, spotIdentifier, status, uidAttendeeArray) => {
  return 'x' + sha256(`${uidEvent}:${spotIdentifier}:${status}:${uidAttendeeArray.join(',')}`).toString().slice(-9);
};

export const canonicalizeSpotIdentifier = (spotIdentifier) => spotIdentifier.trim().replace(/ /g, '-');

export const getRunOffsImages = () => [Image, Image2];

export const getLoadingMessageImages = () => [LoadingMessageImage];

export const getSignedRequest = (httpMethod, url, options, tokenSecret = '') => {
  return axios.get('/server', {
    params: { httpMethod, url, options, tokenSecret }
  }).then((resp) => resp.data.signedRequest);
};

export const createOauthHeader = (params) => {
  let headerStr = "OAuth ";
  headerStr += `oauth_consumer_key="${params.oauth_consumer_key}", `;
  headerStr += `oauth_nonce="${params.oauth_nonce}", `;
  headerStr += `oauth_signature="${params.oauth_signature}", `;
  headerStr += `oauth_signature_method="HMAC-SHA1", `;
  headerStr += `oauth_timestamp="${params.oauth_timestamp}", `;
  headerStr += `oauth_token="${params.oauth_token}", `;
  headerStr += `oauth_version="1.0"`;
  return headerStr;
};

export const makeSignedRequest = (apiEndpoint, requestType = 'GET', data = {}, headers = {}, options={}) => {
  const params = {oauth_token: store.getState().user.oauthToken };
  const reduceData = function (data) {
    const output = [];
    for (let key of Object.keys(data)) {
      output.push(`${key}=${data[key]}`);
    }
    return output.join('&');
  };

  return getSignedRequest(requestType, apiEndpoint, params, store.getState().user.oauthTokenSecret)
    .then((signedRequest) => {
      const oauthHeader = createOauthHeader(signedRequest);
      const requestUrl = apiEndpoint + (requestType === 'GET' ? `?${reduceData(data)}` : '');

      return axios({
        method: requestType,
        url: requestUrl,
        headers: {...headers,  "Authorization": oauthHeader },
        data
      })
        .then((response) => {
          if (response.status >= 200 && response.status < 300) {
            return response.data;
          } else if (response.status === 401) {
            logOutWithAlert();
          } else {
            logFailure('Signed request failed', {...response, requestUrl});
            throw new Error('Something went wrong');
          }
        })
        .catch((error) => {
          if(error.response) {
            const {response} = error;
            const {status} = response;

            if(response.hasOwnProperty('data') && response.data.success) {
              if (options.hasOwnProperty('ignore_error_codes') && options.ignore_error_codes.includes(status)){
                // console.log("Ignored error =>", status);
              } else {
                handleErrors(data.errors);
              }
            } else if (response.status === 401) {
              logOutWithAlert();
            }
          } else {
            const { message } = error;
            handleErrors([{ message }]);
            logException(error, {requestUrl});
          }

          return {error: true};
        });
    });
};

export const performRequest = (apiEndpoint, requestType = 'GET', data = {}, options={}) => {
  const reduceData = function (data) {
    const output = [];
    for (let key of Object.keys(data)) {
      output.push(`${key}=${data[key]}`);
    }
    return output.join('&');
  };

  const requestUrl = apiEndpoint + (requestType === 'GET' ? `?${reduceData(data)}` : '');

  return axios({
    method: requestType,
    url: requestUrl,
    data
  })
    .then((response) => {
      if (response.status >= 200 && response.status < 300) {
        return response.data;
      } else if (response.status === 401) {
        logOutWithAlert();
      } else {
        logFailure('perform request failed', {...response, requestUrl});
        throw new Error('Something went wrong');
      }
    })
    .catch((error) => {
      if(error.response) {
        const {response} = error;
        const {status} = response;

        if(response.hasOwnProperty('data') && response.data.success) {
          if (options.hasOwnProperty('ignore_error_codes') && options.ignore_error_codes.includes(status)){
            //console.log("Ignored error =>", status);
          } else {
            handleErrors(data.errors);
          }
        } else if (response.status === 401) {
          logOutWithAlert();
        }
      } else {
        const { message } = error;
        handleErrors([{ message }]);
        logException(error, {requestUrl});
      }
    });
};

export const logOutUser = () => {
  localStorage.clear();
  window.location = '/';
};

export const checkLoggedIn = () => {
  const { oauthToken, oauthTokenSecret } = store.getState().user;

  // initial check to see if user logged in
  if (!oauthToken && !oauthTokenSecret) {
    window.location = '/login';
  }

  const userInfoUrl = getUserInfoURL();

  makeSignedRequest(userInfoUrl)
    .then(({ status }) => {
      if (status === 401) {
        window.location = '/login';
      }
    });
};

export const userClubMemberId = (uIdClub = UIDCLUB) => {
  const {organizations} = store.getState().user;

  const userClub = organizations.find((organization) => {
    return organization.id === UIDCLUB;
  });

  return userClub ? userClub.memberId : null;
};

export const totalAvailableSpots = () => {
  let availableSpots = 0;
  const {list} = store.getState().parkingSpots;

  Object.keys(list).forEach((parkingSpot) => {
    if (list[parkingSpot].status === "OPEN" ){
      availableSpots += 1 ;
    }
  });

  return availableSpots;
};

export const availableSpots = () => {
  const accommodationInfo = store.getState().userAccommodationInfo;

  return (accommodationInfo.qualifiedSpots - accommodationInfo.reservedSpots.length);
};

export const reservedSpots = () => (
  store.getState().userAccommodationInfo.reservedSpots
);

export const removeElement = (array, element) => {
  let newArray = Object.assign([], array);

  const index = newArray.indexOf(element);
  newArray.splice(index, 1);

  return newArray;
};

export const currentStateForSpot = (spotId) => {
  const { list } = store.getState().parkingSpots;
  return list[canonicalizeSpotIdentifier(spotId)] || {};
};

export const fancyTimeFormat = (time) => {
  const t = parseInt(time);
  return t < 10 ? '0'+t : t;
};

export const pendingTimeToCheckout = (timeStamp) => {
  const pendingTime = (timeStamp - new Date().getTime())/1000;
  if (pendingTime <= 0) {
    return {
      isValid: false,
      time: '00:00 s'
    };
  } else {
    const minutes = Math.floor(pendingTime / 60);
    const seconds = pendingTime - minutes * 60;
    return {
      isValid: true,
      time: `${fancyTimeFormat(minutes)}:${fancyTimeFormat(seconds)} s`
    };
  }
};

export const shouldInitializeDefaultState = () => {
  const { parkingDataFetchedAt } = store.getState().parkingSpots;
  const timeLapse = (new Date().getTime() - parkingDataFetchedAt)/1000;

  return timeLapse > DEFAULT_STATE_INIT_INTERVAL;
};

export const shouldInitializeUserData = () => {
  const { userDataFetchedAt } = store.getState().user;
  const timeLapse = (new Date().getTime() - userDataFetchedAt)/1000;

  return timeLapse > DEFAULT_USER_INIT_INTERVAL;
};

export const geoJsonDataForSpot = (spotId) => {
  const { parkingSpotFeaturesList } = store.getState().session;
  return parkingSpotFeaturesList[spotId] || {};
};

export const checkIsFilteredSpot = (spotId) => {
  const allStates = store.getState();
  const allFilteredSpots = anyFilterSet() ? allStates.search.allFilteredSpots : [];
  const isFilteredSpot = allFilteredSpots.includes(spotId);
  return isFilteredSpot;
};

export const getDetailsForSpot = (spotId) => {
  const spotCurrentState = currentStateForSpot(spotId);
  const geoJsonData = geoJsonDataForSpot(spotId);
  return {
    ...geoJsonData,
    currentState: spotCurrentState
  };
};

export const reserveLimitReached = () => {
  const {reservedSpots, qualifiedSpots} = store.getState().userAccommodationInfo;

  return reservedSpots.length === qualifiedSpots;
};

export const userInfoNotSet = () => !(store.getState().user.firstName);

export const getCentroidFromGeoJson = (coordinates) => {
  const length = coordinates.length;
  const lngLat =  coordinates.reduce((result, coordinates) => {
    return [result[0] + coordinates[0]/length, result[1] + coordinates[1]/length];
  }, [0,0]);
  return {lng: lngLat[0], lat: lngLat[1]};
};

export const getPropertiesForParkingSpot = (spotId) => {
  const {parkingSpots} = store.getState().geoJsonData;
  return parkingSpots.features.find((feature) => {
    return feature.spotid === spotId;
  });
};

export const parkingSpotsGroupedBy = (property, status = null) => {
  let result = {};
  const parkingSpotFeatureLayers = store.getState().geoJsonData.parkingSpots;
  const { blockedSpotsList } = store.getState().session;

  parkingSpotFeatureLayers.forEach((parkingSpotsFeatures) => {
    const {features} = parkingSpotsFeatures.geojson;
    features.forEach((parkingSpot) => {
      const {properties} = parkingSpot;

      if(!blockedSpotsList.includes(properties.spotid)) {
        const property_value = properties[property];

        if(property_value) {
          if (!result[property_value]) {
            result[property_value] = [];
          }

          const currentState = currentStateForSpot(properties.spotid);
          if(status) {
            if (currentState.status === status) {
              result[property_value].push(properties.spotid);
            }
          } else {
            result[property_value].push(properties.spotid);
          }
        }
      }
    });
  });

  return result;
};

export const getSpotsFromFilters = () => {
  const {filterOptions} = store.getState().search;
  return applyFilterOnSpots(filterOptions);
};

export const getAllSpotsFromSelectedFilter = () => {
  const {groupedParkingSpots} = store.getState().parkingSpots;
  return applyFilterOnSpots(groupedParkingSpots);
};

const applyFilterOnSpots = (groupedSpots) => {
  let spotsFromFilterEntities = [];
  const {appliedFilters} = store.getState().search;

  Object.keys(appliedFilters).forEach((filterEntity) => {
    const groupedSpotsForEntity = groupedSpots[filterEntity];

    // consider all filters when appliedFilters for that entity has blank array.
    let filterValuesToConsider = appliedFilters[filterEntity];
    if (filterValuesToConsider.length === 0) {
      filterValuesToConsider = Object.keys(groupedSpotsForEntity);
    }

    // Take union of all the filtered spots achieved for a particular filter entity.
    // eg: Take all filtered spots for sizes applied.
    const filteredSpotsForEntity = filterValuesToConsider.reduce((union, filter) => {
      const parkingSpotsForFilter = groupedSpotsForEntity[filter];
      const filterSet = new Set(parkingSpotsForFilter);
      return new Set([...union, ...filterSet]);
    }, new Set([]));

    spotsFromFilterEntities.push(filteredSpotsForEntity);
  });

  // Take intersection of all the filtered spots across various filter entities
  const filteredSpotsIntersection =  spotsFromFilterEntities.reduce((intersection, filteredSpotsForEntity) => {
    return new Set(
      [...intersection].filter(spotId => filteredSpotsForEntity.has(spotId))
    );
  }, (spotsFromFilterEntities[0] || new Set([])));

  return [...filteredSpotsIntersection];
};

export const getCentroidForSpot = (spotId) => {
  const {parkingSpots} = store.getState().geoJsonData;

  let centroid = [];
  parkingSpots.forEach(spotDetails => {
    const {features} = spotDetails.geojson;
    const spotFeature =  features.find((feature) => { return feature.properties.spotid === spotId ;});

    if(spotFeature) {
      centroid =  getCentroidFromGeoJson(spotFeature.geometry.coordinates[0]);
    }
  });

  return centroid;
};


export const priceInDollars = (priceInCents) => {
  const dollars = priceInCents / 100 ;
  return dollars.toLocaleString("en-US", {style:"currency", currency:"USD"});
};

export const handleErrors = (errors) => {
  errors.forEach(error =>  showToasterError(error.message));
};

export const appendMsgToToaster = (obj) => {
  store.dispatch(addToToaster({
    id: UUID.get('__toaster__'),
    ...obj
  }));
};

export const showToasterError = (message, options={}) => {
  appendMsgToToaster({ type: 'error', message, ...options });
};

export const showToasterSuccess = (message, options={}) => {
  appendMsgToToaster({ type: 'success', message, ...options });
};

export const showToasterWarning = (message, options={}) => {
  appendMsgToToaster({ type: 'warning', message, ...options });
};

export const showToasterInfo = (message, options={}) => {
  appendMsgToToaster({ type: 'info', message, ...options });
};

export const showAppUpdateToaster = (message="", options={}) => {
  appendMsgToToaster({ type: 'toaster-app-update', message, ...options });
};

export const showLocalStorageFullToaster = (message="", options={}) => {
  appendMsgToToaster({ type: 'local-storage-full', message, ...options });
};

export const hasAvailableSpots = () => {
  const { qualifiedSpots, reservedSpots, heldSpot } = store.getState().userAccommodationInfo;
  const reservedOrHeld = heldSpot ? (reservedSpots.length + 1) : reservedSpots.length;

  return qualifiedSpots - reservedOrHeld;
};

export const isCurrentCheckoutEvent = (event) => (event === store.getState().checkout.event);

export const logOutWithAlert = () => {
  showToasterError("You have been logged out. Please login again.");

  setTimeout(() => {
    logOutUser();
  }, 5000);

};

export const anyFilterSet = () => {
  const {appliedFilters} = store.getState().search;

  let filtersSet = false;

  Object.keys(appliedFilters).forEach((filterEntity) => {
    if(appliedFilters[filterEntity].length) {
      filtersSet = true;
    }
  });

  return filtersSet;
};

export const userCanReserveOrTradeSpot = () => {
  const {preBookedSpots} = store.getState().parkingSpots;
  const {reservedSpots, qualifiedSpots} = store.getState().userAccommodationInfo;

  const canReserveSpots = reservedSpots.length < qualifiedSpots;

  if(canReserveSpots) return true;

  return _.difference(reservedSpots, preBookedSpots).length > 0;
};

window.parkingAreaMarkersEnabled = (showParkingAreaMarker) => {
  store.dispatch({
    type: ActionTypes.TOGGLE_PARKING_AREA_MARKERS,
    showParkingAreaMarker,
  });
};

window.parkingSpotMarkersEnabled = (showParkingSpotMarker) => {
  store.dispatch({
    type: ActionTypes.TOGGLE_PARKING_SPOT_MARKERS,
    showParkingSpotMarker,
  });
};

window.parkingSpotMarkersLabelEnabled = (showParkingSpotMarkerLabel) => {
  store.dispatch({
    type: ActionTypes.TOGGLE_PARKING_SPOT_MARKERS_LABEL,
    showParkingSpotMarkerLabel,
  });
};

export const dedupeArray = (array, keys) => {
  const newSet = new Set();

  return array.reduce((acc, item) => {
    const mappedKeys = keys.map((key) => item[key]).join('|')
    if (!newSet.has(mappedKeys)) {
      newSet.add(mappedKeys)
      acc.push(item)
    }

    return acc
  }, [])
};



// WEBPACK FOOTER //
// ./src/utils/index.js